"use client";

import React from 'react';
import { RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis } from 'recharts';
import { Shield, TrendingUp } from 'lucide-react';
import { useLanguage } from '@/lib/language-context';
import { t } from '@/lib/translations';

export default function SecurityHealthScore() {
  const { language } = useLanguage();
  const score = 74;

  const getScoreColor = (s: number) => {
    if (s >= 85) return '#00E676';
    if (s >= 65) return '#FFB800';
    return '#FF3B3B';
  };

  const getScoreLabel = (s: number) => {
    if (s >= 85) return t(language, 'health_good');
    if (s >= 65) return t(language, 'health_warning');
    return t(language, 'health_critical');
  };

  const color = getScoreColor(score);
  const data = [{ value: score, fill: color }];

  return (
    <div className="cyber-card h-full p-5 flex flex-col items-center justify-center relative overflow-hidden min-h-[200px]">
      {/* Background glow */}
      <div
        className="absolute inset-0 opacity-5 rounded-xl"
        style={{ background: `radial-gradient(circle at center, ${color}, transparent 70%)` }}
      />

      {/* Icon */}
      <div className="flex items-center gap-2 mb-3 self-start w-full">
        <Shield size={14} className="text-cyber-muted" />
        <span className={`text-[11px] text-cyber-muted uppercase tracking-widest font-mono ${language === 'bn' ? 'font-bangla normal-case tracking-normal' : ''}`}>
          {t(language, 'health_score')}
        </span>
      </div>

      {/* Radial Chart */}
      <div className="relative w-40 h-40">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            innerRadius="70%"
            outerRadius="100%"
            data={data}
            startAngle={220}
            endAngle={-40}
          >
            <PolarAngleAxis
              type="number"
              domain={[0, 100]}
              angleAxisId={0}
              tick={false}
            />
            <RadialBar
              dataKey="value"
              cornerRadius={4}
              background={{ fill: '#1A2035' }}
            />
          </RadialBarChart>
        </ResponsiveContainer>

        {/* Center Score */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            className="text-4xl font-bold font-mono tabular-nums"
            style={{ color, textShadow: `0 0 20px ${color}60` }}
          >
            {score}
          </span>
          <span className="text-[10px] text-cyber-muted font-mono">/100</span>
        </div>
      </div>

      {/* Label */}
      <div
        className="mt-2 px-3 py-1 rounded-full text-xs font-medium border"
        style={{
          backgroundColor: `${color}15`,
          borderColor: `${color}40`,
          color,
        }}
      >
        {getScoreLabel(score)}
      </div>

      {/* Trend */}
      <div className="flex items-center gap-1.5 mt-3 text-[11px] text-cyber-muted">
        <TrendingUp size={11} className="text-yellow-400" />
        <span className="font-mono">+2.1 from yesterday</span>
      </div>

      {/* Score breakdown mini */}
      <div className="w-full mt-4 space-y-1.5">
        {[
          { label: 'Endpoint', value: 82 },
          { label: 'Network', value: 68 },
          { label: 'Compliance', value: 71 },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-2">
            <span className="text-[10px] text-cyber-muted w-16 font-mono">{item.label}</span>
            <div className="flex-1 h-1 bg-cyber-border rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${item.value}%`,
                  backgroundColor: getScoreColor(item.value),
                }}
              />
            </div>
            <span className="text-[10px] font-mono" style={{ color: getScoreColor(item.value) }}>
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}